<template>
    <v-navigation-drawer
        id="navigation-drawer"
        v-model="drawer"
        :rail="rail"
        theme="dark"
        persistent
        permanent
        :key="navbarKey"
        :width="350"
    >
        <div class="menu-opener-holder">
            <v-btn class="menu-opener" size="small" icon="mdi-menu" @click.stop="toggleMenu" />
        </div>
        <v-list>
            <div v-for="item in items" :key="item.title" class="group-wrapper">
                <v-list-item
                    v-if="!item.child && !item.header"
                    :to="item.href"
                    :title="item.title"
                    :prepend-icon="item.icon"
                />
                <div class="nav-header" v-else-if="item.header">{{ item.title }}</div>
                <v-list-group v-else>
                    <template #activator="{ props }">
                        <div class="has-submenu-wrapper" @click="openSubMenu">
                            <v-list-item
                                class="has-submenu"
                                :key="item.title"
                                v-bind="props"
                                :title="item.title"
                                :prepend-icon="item.icon"
                            />
                        </div>
                    </template>
                    <div v-for="subMenu in item.child" :key="subMenu.title">
                        <v-list-item
                            v-if="!subMenu.child"
                            :to="subMenu.href"
                            :key="subMenu.title"
                            :title="subMenu.title"
                            :class="subMenu?.class ? subMenu.class : ''"
                            :prepend-icon="subMenu.icon"
                        />
                        <v-list-group v-else>
                            <template #activator="{ props }">
                                <v-list-item
                                    :key="subMenu.title"
                                    v-bind="props"
                                    :title="subMenu.title"
                                    :prepend-icon="subMenu.icon"
                                />
                            </template>
                            <div v-for="deepSubMenu in subMenu.child" :key="deepSubMenu.title">
                                <v-list-item
                                    :to="deepSubMenu.href"
                                    :key="deepSubMenu.title"
                                    :title="deepSubMenu.title"
                                    :prepend-icon="deepSubMenu.icon"
                                />
                            </div>
                        </v-list-group>
                    </div>
                </v-list-group>
            </div>
        </v-list>
    </v-navigation-drawer>
</template>

<script>
    import { get } from 'lodash';
    import { mapGetters, mapState } from 'vuex';

    import {
        FEATURE_FACEBOOK_AD_ACCOUNT,
        FEATURE_FACEBOOK_MARKETPLACE,
        FEATURE_MICROSOFT_AD_ACCOUNT,
        FEATURE_PINTEREST_AD_ACCOUNT,
        FEATURE_SNAPCHAT_AD_ACCOUNT,
        FEATURE_TIKTOK_AD_ACCOUNT,
    } from '@/helpers/globals';

    import getItems from './items';

    export default {
        props: {
            menuOpen: {
                type: Boolean,
                required: true,
            },
            overlayDrawer: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                navbarKey: 0,
                activeSubItem: '',
                items: [],
                drawer: true,
                rail: true,
            };
        },
        computed: {
            ...mapState({
                user: (state) => state.user.user,
                currentDealer: (state) => state.dealer.currentDealer,
                currentAgency: (state) => state.agency.currentAgency,
            }),
            ...mapGetters([
                'userHasRoles',
                'userAgency',
                'userIsClient',
                'dealerHasFeatures',
                'dealerHasFeature',
                'dealerHasVertical',
            ]),
            platforms() {
                return {
                    facebook: {
                        ads: this.dealerHasFeature(FEATURE_FACEBOOK_AD_ACCOUNT),
                        marketplace: this.dealerHasFeature(FEATURE_FACEBOOK_MARKETPLACE),
                    },
                    microsoft: this.dealerHasFeature(FEATURE_MICROSOFT_AD_ACCOUNT),
                    snapchat: this.dealerHasFeature(FEATURE_SNAPCHAT_AD_ACCOUNT),
                    tiktok: this.dealerHasFeature(FEATURE_TIKTOK_AD_ACCOUNT),
                    pinterest: this.dealerHasFeature(FEATURE_PINTEREST_AD_ACCOUNT),
                };
            },
        },
        watch: {
            user() {
                this.setItems();
            },
            currentDealer() {
                this.setItems();
            },
            currentAgency() {
                this.setItems();
            },
            rail(val) {
                this.$emit('toggle-menu', !val);
            },
            menuOpen(val) {
                this.rail = !val;
            },
        },
        created() {
            this.setItems();
            this.rail = !this.menuOpen;
        },
        methods: {
            toggleMenu() {
                this.rail = !this.rail;
            },
            openSubMenu() {
                if (this.rail) {
                    this.rail = false;
                }
            },
            setItems() {
                const userIsClient = this.userHasRoles(['client']);
                const items = getItems(this.currentDealer, this.currentAgency, this.platforms, userIsClient);
                this.items = items.filter(this.filterItems);
                this.navbarKey += 1;
            },
            filterItems(item) {
                // If the items has roles specified override all other logic
                if (item.roles) {
                    return !!this.userHasRoles(item.roles);
                }

                // If there's no link but the item has children filter them
                if (!item.href && item.child) {
                    // Run this function recursively to set the children while telling
                    // Detecting whether the result has children which will filter out entire branches
                    return (item.child = item.child.filter(this.filterItems)).length;
                }

                // Ensure the to property has the proper structure to check roles & features
                if (typeof item.href === 'object' && item.href.name) {
                    const route = this.getRoute(item.href.name);
                    const hasAgencyOverride = this.hasAgencyOverride(route);
                    const verticals = this.getRouteVerticals(route);
                    // Check if the route is locked to verticals for use downstream
                    let dealerHasVerticals = null;
                    if (verticals.length) {
                        dealerHasVerticals = verticals.some((vertical) => this.dealerHasVertical(vertical));
                    }
                    const features = this.getRouteFeatures(route);
                    // Check if the route is locked to features for use downstream
                    let dealerHasFeatures = null;
                    if (features.length) {
                        dealerHasFeatures = this.dealerHasFeatures(features);
                    }
                    // Get the roles from the route and determine if the route is locked to
                    // specified roles then determine if the active user has this role (or
                    // their agency has an active override) and their current dealer satisfies
                    // the feature and/or vertical requirements (if set)
                    const roles = this.getRouteRoles(route);
                    if (roles.length) {
                        return (
                            (this.userHasRoles(roles) || hasAgencyOverride) &&
                            (dealerHasFeatures === null || dealerHasFeatures === true) &&
                            (dealerHasVerticals === null || dealerHasVerticals === true)
                        );
                    }
                    // If the vertical requirements have been specified use them
                    if (dealerHasVerticals !== null) {
                        return dealerHasVerticals;
                    }
                    // If feature requirements have been specified use them
                    if (dealerHasFeatures !== null) {
                        return dealerHasFeatures;
                    }
                    // One last check before finishing to see if the agency
                    // can see the route (if specified)
                    if (hasAgencyOverride) {
                        return true;
                    }
                }
                // If we make it here continue recursively filtering out children
                if (item.child) {
                    return (item.child = item.child.filter(this.filterItems)).length;
                }
            },
            hasAgencyOverride(route) {
                // Ensure there's a route and user agency to proceed
                if (!route || !this.userAgency) {
                    return false;
                }

                const agencies = get(route, 'meta.agencies', null) || [];

                if (!this.userIsClient && agencies.includes(this.userAgency.id)) {
                    return true;
                }

                return false;
            },
            getRoute(name) {
                return this.$router.options.routes.find((route) => route.name === name);
            },
            getRouteRoles(route) {
                if (!route) {
                    return [];
                }
                // If there's route roles return them
                if (route.meta && route.meta.roles && route.meta.roles.length) {
                    return route.meta.roles;
                }
                // Otherwise return an empty array
                return [];
            },
            getRouteFeatures(route) {
                return route?.meta?.features || [];
            },
            getRouteVerticals(route) {
                return route?.meta?.verticals || [];
            },
        },
    };
</script>

<style lang="scss">
    #navigation-drawer {
        padding-top: 69px;
        background: #4e555b;
        .bb-meta {
            background-image: url('@/assets/icons/meta.svg');
            background-position: center center;
            background-size: contain;
            filter: brightness(0) invert(1);
        }
        .bb-snapchat {
            background-image: url('@/assets/icons/snapchat.svg');
            background-position: center center;
            background-size: contain;
            filter: brightness(0) invert(1);
        }
        .bb-tiktok {
            background-image: url('@/assets/icons/tiktok.svg');
            background-position: center center;
            background-size: contain;
            filter: brightness(0) invert(1);
        }
        .bb-pinterest {
            background-image: url('@/assets/icons/pinterest.svg');
            background-position: center center;
            background-size: contain;
            filter: brightness(0) invert(1);
        }
        .v-list-item--variant-text {
            .v-list-item__overlay {
                background: transparent;
            }
            &:not(.v-list-item--active):hover {
                .v-list-item__overlay {
                    background: white;
                }
            }
        }
        .v-list-item-title {
            text-transform: uppercase;
            font-weight: 600;
            color: #8f9ea6;
        }
        .v-list-item--active:not(.v-list-group__header) .v-list-item-title {
            color: white;
        }
        .v-list-group {
            --prepend-width: 0px;
        }
        .v-list-item__prepend > .v-icon ~ .v-list-item__spacer {
            width: 20px;
        }
        .menu-opener-holder {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            z-index: 9;
            padding: 8px 0;
            border-bottom: 1px solid $gray-dark;
        }
        .menu-opener {
            padding: 10px 5px;
            background-color: rgba(0, 0, 0, 0.1);
            i {
                transform: scale(1.5);
            }
        }
        .has-submenu-wrapper {
            & + .v-list-group__items {
                background-color: #3c4347;
            }
        }
        .has-submenu-wrapper .v-list-item--active {
            background-color: var(--color-primary);
            .v-list-item-title {
                color: white;
            }
        }
        .nav-header {
            color: #8f9ea6;
            border-bottom: 1px solid #8f9ea6;
            margin: 50px 20px 20px;
            padding: 0 0 5px;
            font-size: 14px;
            font-weight: 600;
            white-space: nowrap;
            text-transform: uppercase;
        }
        .group-wrapper > .v-list-item--active {
            background-color: var(--color-primary);
        }
    }
    // when closed
    .v-navigation-drawer--rail {
        .nav-header {
            display: none;
        }
        .has-submenu-wrapper {
            cursor: pointer;
            & + .v-list-group__items {
                display: none;
            }
        }
        .has-submenu {
            pointer-events: none;
        }
    }
    .admin-badge:before {
        content: 'Admin';
        position: absolute;
        top: 15px;
        right: 15px;
        background: black;
        color: white;
        padding: 2px 5px;
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
    }
</style>
